import { format, parseISO } from 'date-fns';

import type { LogEntry } from '@/types/log-entry';

/**
 * Recent Scouts collapsed-tally derivation (DASH-S1 / issue #786).
 *
 * Pure function over a `LogEntry[]` list. Produces the icon-free collapsed
 * tally string: `{nBuck} buck · {nDoe} doe · {nSign} sign · {nPhotos} photos · last {Mon D}`.
 *
 * Discriminator (A3 / G4 — source: pattern-analysis-service.ts uses
 * `animalObserved === true` for sightings): a SIGHTING is `animalObserved === true`;
 * a SIGN row is the complement `animalObserved !== true` (groups `false` AND
 * `undefined`). The tally counts each entry EXACTLY ONCE via this discriminator —
 * NEVER per-tag (A1 / RULE-K). `nSign = entries.filter(e => e.animalObserved !== true).length`.
 *
 * Sex counts (`buck`/`doe`) are only segments when `> 0`. `unknown`-sex sightings
 * are NOT printed as "unknown" — they roll into neither the buck nor doe count
 * (spec line 126/206: "don't print unknown").
 */

export interface RecentScoutsTally {
  nBuck: number;
  nDoe: number;
  nSign: number;
  nPhotos: number;
  /** ISO date of the most-recent entry, or null when the list is empty. */
  lastDate: string | null;
  /** The fully-assembled tally line, or null when the list is empty. */
  text: string | null;
}

/**
 * `true` when the entry is a SIGN row (not an affirmative animal observation).
 * The complement of the `pattern-analysis-service` sighting definition
 * (`animalObserved === true`). Replicated inline by design (A3) — the row
 * component and the card use the same predicate.
 */
export function isSignEntry(entry: LogEntry): boolean {
  return entry.animalObserved !== true;
}

export function deriveRecentScoutsTally(entries: LogEntry[]): RecentScoutsTally {
  let nBuck = 0;
  let nDoe = 0;
  let nSign = 0;
  let nPhotos = 0;
  let lastDate: string | null = null;

  for (const entry of entries) {
    if (isSignEntry(entry)) {
      nSign += 1;
    } else if (entry.animalSex === 'buck') {
      nBuck += 1;
    } else if (entry.animalSex === 'doe') {
      nDoe += 1;
    }
    // fawn / unknown / no-sex sightings: counted in neither buck nor doe
    // (spec: do not print "unknown"); they are still real sightings, so they
    // are NOT folded into nSign.

    nPhotos += entry.photos?.length ?? 0;

    if (lastDate === null || entry.date.localeCompare(lastDate) > 0) {
      lastDate = entry.date;
    }
  }

  if (entries.length === 0) {
    return { nBuck, nDoe, nSign, nPhotos, lastDate: null, text: null };
  }

  const segments: string[] = [];
  if (nBuck > 0) segments.push(`${nBuck} buck`);
  if (nDoe > 0) segments.push(`${nDoe} doe`);
  if (nSign > 0) segments.push(`${nSign} sign`);
  segments.push(`${nPhotos} photos`);
  if (lastDate) segments.push(`last ${format(parseISO(lastDate), 'MMM d')}`);

  return {
    nBuck,
    nDoe,
    nSign,
    nPhotos,
    lastDate,
    text: segments.join(' · '),
  };
}
